// @givmo/app-admin — the server's price for a company card's gift-card charge (GC-FU31). ★ MONEY ★
//
// Signed Terms V6 clause 5 promises card processing is "itemized for you before you pay", and the
// plan's exit checklist requires the gross at checkout. A card-paid gift card is charged its face
// plus a processor gross-up the company always covers (D-GC-2), priced by the brand of the stored
// card, so the send dialogs never work it out themselves: every figure they state comes from
//
//   GET /admin/company/gift-cards/quote?amount=&count=&card_id=
//
// which prices ONE charge — a single send is one card, a batch one charge for all of its cards
// (D-EP-16, never N single charges) — with the pricing both charges use. The send carries the gross
// and fee it stated (`expected_amount_charged` / `expected_fee`), and a backend that would charge
// other figures refuses with `409 CHARGE_QUOTE_CHANGED` before money moves, as the Give, approve and
// deposit dialogs' charges are held (`components/charge-quote.ts`). Never price from
// `GET /fees/processor`: it withholds the card-brand rows by design.
//
// The route is newer than the committed OpenAPI snapshot, so it is called through the configured
// client (`agent_docs/api-client.md` § "A whole ROUTE the snapshot lacks"). The decode FAILS CLOSED:
// a field missing or mistyped, a quote for another face or count, or figures that do not add up
// make the whole quote unusable, and the dialog states no figure and holds Send.

import { givmoClient } from '@givmo/api-client';
import { parseCentsAmount } from '../dashboard/dashboard-decode';

/** The gift-card quote's route. */
export const GIFT_CARD_QUOTE_URL = '/admin/company/gift-cards/quote';

/** One card charge, priced — money as the exact decimal strings served. */
export interface GiftCardQuote {
  /** Each card's face, as served. */
  readonly amount: string;
  /** How many cards the one charge pays for. */
  readonly count: number;
  /** What the cards carry together (face × count). */
  readonly faceTotal: string;
  /** The gross the company card is charged. */
  readonly amountCharged: string;
  /** The card processing added on top of the face total. */
  readonly fee: string;
}

/** The TanStack Query key for a quote: a different face, count or card is a different price. */
export function giftCardQuoteQueryKey(
  amount: number,
  count: number,
  cardId: string,
): readonly unknown[] {
  return ['company-gift-card-quote', amount, count, cardId];
}

/** Read a quote for `count` cards of the whole-dollar `amount` each on the saved card `cardId`. */
export async function fetchGiftCardQuote(
  amount: number,
  count: number,
  cardId: string,
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: GIFT_CARD_QUOTE_URL,
    query: { amount: String(amount), count, card_id: cardId },
    throwOnError: true,
  });
  return data;
}

const PLAIN_DECIMAL = /^\d+(?:\.\d+)?$/;

function decimalField(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === 'string' && PLAIN_DECIMAL.test(value) ? value : null;
}

/**
 * Decode a quote (`CompanyGiftCardQuoteOut`) for the charge a dialog is about to make — `count` cards
 * of the whole-dollar `amount` each — failing closed: any bad field, a quote for another face or
 * count, or a gross that is not the face total plus the fee → `null`.
 */
export function toGiftCardQuote(
  body: unknown,
  expected: { amount: number; count: number },
): GiftCardQuote | null {
  if (typeof body !== 'object' || body === null) return null;
  const row = body as Record<string, unknown>;
  const amount = decimalField(row, 'amount');
  const faceTotal = decimalField(row, 'face_total');
  const amountCharged = decimalField(row, 'amount_charged');
  const fee = decimalField(row, 'fee');
  if (amount === null || faceTotal === null || amountCharged === null || fee === null) return null;
  if (typeof row.count !== 'number' || row.count !== expected.count) return null;

  const amountCents = parseCentsAmount(amount);
  const faceTotalCents = parseCentsAmount(faceTotal);
  const chargedCents = parseCentsAmount(amountCharged);
  const feeCents = parseCentsAmount(fee);
  if (
    amountCents === null ||
    faceTotalCents === null ||
    chargedCents === null ||
    feeCents === null ||
    amountCents !== expected.amount * 100 ||
    faceTotalCents !== amountCents * expected.count ||
    chargedCents !== faceTotalCents + feeCents
  ) {
    return null;
  }
  return { amount, count: row.count, faceTotal, amountCharged, fee };
}

/** The stated figures a card send carries, so the backend refuses any other (`CHARGE_QUOTE_CHANGED`). */
export interface StatedGiftCardCharge {
  expected_amount_charged: string;
  expected_fee: string;
}

/** The body fields that hold a card send to the quote the admin was shown. */
export function statedGiftCardCharge(quote: GiftCardQuote): StatedGiftCardCharge {
  return { expected_amount_charged: quote.amountCharged, expected_fee: quote.fee };
}
